import { EMPTY_OBJECT } from 'src/globals/globals';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { TaskList } from 'src/app/model/task-list/task-list';
import { PlanningDateService } from 'src/app/services/planning-date-service';
import { AuthService } from 'src/app/services/auth.service';
import * as moment from 'moment';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { SubProjectTask } from 'src/app/model/task-list/subproject-task';
import { BaseProject } from 'src/app/model/project/base-project';
import { Adjustment } from 'src/app/model/project/adjustment';
import { StringHelper } from 'src/app/helpers/string-helper';
import ProjectHelper from 'src/app/helpers/project-helper';
import Company from 'src/app/model/company/company';
import { User } from 'src/app/model/user';
import { catchError, startWith, switchMap } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { Router } from '@angular/router';
import { Task, TASK_TYPES } from 'src/app/model/task-list/task';
import { PlacerTasksService } from 'src/app/services/placer-tasks.service';
import ProductHelper from 'src/app/helpers/product-helper';
import Board from 'src/app/model/product/board/board';
import { PlacerSubmitService } from 'src/app/services/placer-offline-changes/placer-submit.service';
import {
  OfflineSubProjectChangeObject,
  PlacerSubProjectChangesService,
} from 'src/app/services/placer-offline-changes/placer-subproject-changes.service';
import {
  OfflineProductChangeObject,
  PlacerProductChangesService,
} from 'src/app/services/placer-offline-changes/placer-product-changes.service';
import { SubProject } from 'src/app/model/project/sub-project';
import Address from 'src/app/model/project/address';
import { OfflineProjectStorageService } from 'src/app/services/offline-project-storage.service';
import { OtherTask } from 'src/app/model/task-list/other-task';
import { SubmitEventService } from 'src/app/services/placer-offline-changes/submit-event.service';
import SubmitQueueHelper from 'src/app/helpers/offline-submit/submit-queue-helper';
import PlacerPlanningViewGeneralTaskHelper, {
  PlacerPlanningViewContext,
} from './helpers/placer-planning-view-general-task-helper';
import PlacerPlanningViewLoadTaskHelper from './helpers/placer-planning-view-load-task-helper';
import PlacerPlanningViewOtherTaskHelper from './helpers/placer-planning-view-other-task-helper';
import { LoadTask } from 'src/app/model/task-list/load-task';
import { FormatMoment } from 'src/app/pipes/format-moment';
import { PlacerTaskChangesService } from 'src/app/services/placer-offline-changes/task/offline-storage/placer-task-changes.service';
import { PlacerTaskSubmitService } from 'src/app/services/placer-offline-changes/task/submit/placer-task-submit.service';
import { OfflineTaskChangesObject } from 'src/app/services/placer-offline-changes/task/offline-storage/offline-task-changes-object';
import TaskAddressHelper from '../admin-planning-container/daily-planning-overview/task-address-helper';

@Component({
  selector: 'placer-planning-view',
  templateUrl: './placer-planning-view.component.html',
  styleUrls: ['./placer-planning-view.component.scss'],
  providers: [FormatMoment],
})
export class PlacerPlanningViewComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy
{
  taskList: TaskList;
  private user: User;
  private date: Date;
  subProjects: SubProject[] = [];
  addresses: Address[] = [];

  private getTasksSubject = new Subject<any>();

  private placerPlanningViewContext: PlacerPlanningViewContext;

  EMPTY_OBJECT = EMPTY_OBJECT;

  selectedView: SELECTABLE_VIEWS;
  SELECTABLE_VIEWS = SELECTABLE_VIEWS;

  constructor(
    private authService: AuthService,
    private router: Router,
    private planningDateService: PlanningDateService,
    private placerTasksService: PlacerTasksService,
    private offlineProjectStorageService: OfflineProjectStorageService,
    private placerSubProjectChangesService: PlacerSubProjectChangesService,
    private placerProductChangesService: PlacerProductChangesService,
    private placerSubmitService: PlacerSubmitService,
    private placerTaskChangesService: PlacerTaskChangesService,
    private placerTaskSubmitService: PlacerTaskSubmitService,
    private submitEventService: SubmitEventService,
    private formatMoment: FormatMoment
  ) {
    super();
  }

  ngOnInit() {
    this.placerPlanningViewContext = {
      formatMoment: this.formatMoment,
    };
    this.addSubscription(
      this.getTasksSubject.pipe(
        startWith({}),
        switchMap(() => {
          this.taskList = EMPTY_OBJECT;
          if (
            this.user == null ||
            this.user.id == null ||
            this.user.employee == null ||
            this.user.employee.id == null ||
            this.date == null
          ) {
            return of({});
          }
          return this.placerTasksService.getTaskList(
            this.user.employee.id,
            moment(this.date).format('YYYYMMDD')
          );
        }),
        catchError(() => {
          return of({});
        })
      ),
      async (res) => {
        this.taskList = res;
        await this.preprocessTasks();
        if (this.taskList != null) {
          PlacerPlanningViewGeneralTaskHelper.sortCompletedTasks(
            this.taskList.tasks
          );
        }
      }
    );
    this.addSubscription(
      this.authService.getAuthenticatedUserSubscribable(),
      (user: User) => {
        this.user = user;
        this.getTasksSubject.next();
      }
    );
    this.addSubscription(
      this.planningDateService.getSelectedPlanningDateSubscribable(),
      (date: Date) => {
        this.date = date;
        this.getTasksSubject.next();
      }
    );
    this.addSubscription(
      this.submitEventService.getSubmitInProgressChangedSubscribable(),
      () => {
        this.setSubmitInProgressInfo();
      }
    );
    const submitQueueObservable = this.createObservableUntilDestroy(
      this.placerSubmitService.getToSubmitChangedSubscribable(),
      this.placerTaskSubmitService.getToSubmitChangedSubscribable()
    );
    this.addSubscription(submitQueueObservable, () => {
      this.setSubmitQueuedInfo();
    });

    this.selectedView = SELECTABLE_VIEWS.PLACER_TOUR_LIST_VIEW;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.getTasksSubject.complete();
    this.getTasksSubject = null;
  }

  async preprocessTasks(): Promise<void> {
    this.subProjects = [];
    this.addresses = [];

    if (
      this.taskList == null ||
      !CollectionHelper.isPopulated(this.taskList.tasks)
    ) {
      return;
    }

    this.setSubmitInfo();
    const taskIdToChanges = await this.getOfflineTaskChanges();

    // Process all tasks first and collect subprojects
    const newSubProjects: SubProject[] = [];

    await Promise.all(
      this.taskList.tasks.map(async (t) => {
        if (t.type === 'SubProjectTask') {
          const subProjectTask = <SubProjectTask>t;
          if (
            !this.taskList.fromOnline &&
            subProjectTask.subProject.project != null
          ) {
            const storedProject =
              this.offlineProjectStorageService.getStoredProject(
                subProjectTask.subProject.project.id
              );
            if (
              storedProject != null &&
              CollectionHelper.isPopulated(storedProject.subProjects)
            ) {
              const storedMatchingSubProject = storedProject.subProjects.find(
                (sp) => sp.id === t.subProject.id
              );
              subProjectTask.subProject = storedMatchingSubProject;
              subProjectTask.subProject.project = storedProject;
              this.updateExecutionInfo(subProjectTask.subProject);
            }
          }
          this.calculateSubProjectExecuted(subProjectTask);
          this.calculateSubProjectExecutedIconClass(subProjectTask);
          if (
            subProjectTask.subProject != null &&
            subProjectTask.subProject.project != null
          ) {
            // Collect subproject instead of pushing directly
            if (subProjectTask.subProject) {
              newSubProjects.push(subProjectTask.subProject);
            }
            const involvedCompanies: Company[] =
              ProjectHelper.getDtoInvolvedCompanies(
                subProjectTask.subProject.project
              );
            subProjectTask.companiesDisplay = StringHelper.join(
              involvedCompanies.map((c) => c.regularName),
              ', '
            );
            const address = subProjectTask.subProject.projectPlacement.address;
            subProjectTask.addressLine1 = address.line1;
            subProjectTask.addressLine2 = StringHelper.join(
              [address.postalCode, address.city],
              ' '
            );
            subProjectTask.onlyAddressLine1 = StringHelper.isEmpty(
              subProjectTask.addressLine2
            );
            subProjectTask.addressLine1 += subProjectTask.onlyAddressLine1
              ? ''
              : ',';
            this.calculatePlacerNotes(subProjectTask);
            this.calculateBoardTypeAndPlacement(subProjectTask);
            this.calculateAppointment(subProjectTask);
          }
        } else if (t.type === TASK_TYPES.OTHER_TASK) {
          PlacerPlanningViewOtherTaskHelper.preprocessOtherTask(
            <OtherTask>t,
            taskIdToChanges
          );
        } else if (t.type === TASK_TYPES.LOAD_TASK) {
          PlacerPlanningViewLoadTaskHelper.preprocessLoadTask(
            <LoadTask>t,
            this.placerPlanningViewContext,
            taskIdToChanges
          );
        }
      })
    );

    // Update subProjects and addresses after all processing is done
    this.subProjects = newSubProjects;
    this.calculateMapAddresses();
  }

  private async getOfflineTaskChanges(): Promise<
    Map<number, OfflineTaskChangesObject>
  > {
    const ids = this.taskList.tasks.map((t) => t.id);
    const objects = await this.placerTaskChangesService.getObjects(ids);
    return CollectionHelper.toNonOptionalIdMap(objects);
  }

  private async setSubmitInfo(): Promise<void> {
    const inProgress = await this.submitEventService.getSubmitInProgressAsync();
    const queued = await this.placerSubmitService.getToSubmit();
    const tasksQueued = await this.placerTaskSubmitService.getToSubmit();
    SubmitQueueHelper.processSubmitInfo(
      this.taskList.tasks,
      inProgress,
      queued,
      tasksQueued
    );
  }

  private async setSubmitInProgressInfo(): Promise<void> {
    const inProgress = await this.submitEventService.getSubmitInProgressAsync();
    SubmitQueueHelper.processSubmitInProgress(this.taskList.tasks, inProgress);
  }

  private async setSubmitQueuedInfo(): Promise<void> {
    const queued = await this.placerSubmitService.getToSubmit();
    const tasksQueued = await this.placerTaskSubmitService.getToSubmit();
    SubmitQueueHelper.processSubmitQueued(
      this.taskList.tasks,
      queued,
      tasksQueued
    );
  }

  updateExecutionInfo(subProject: SubProject) {
    const offlineSubProjectChanges =
      this.placerSubProjectChangesService.getDirectOfflineSubProjectChangeObject(
        subProject.id
      );
    if (
      offlineSubProjectChanges != null &&
      CollectionHelper.isPopulated(
        offlineSubProjectChanges.registeredActions
      ) &&
      subProject.projectPlacement != null
    ) {
      if (subProject.projectPlacement.registeredActions == null) {
        subProject.projectPlacement.registeredActions = [];
      }
      subProject.projectPlacement.registeredActions.push(
        ...offlineSubProjectChanges.registeredActions
      );
      subProject.projectPlacement.registeredActions.sort(
        GeneralHelper.compareRegisteredActionsDesc
      );
    }
    if (subProject.type === 'BaseProject') {
      const productChanges =
        subProject.project == null ||
        !CollectionHelper.isPopulated(subProject.project.products)
          ? []
          : this.placerProductChangesService.getDirectOfflineProductChangeObjects(
              subProject.project.products.map((p) => p.id)
            );
      this.updateBaseProjectExecutionInfo(subProject, productChanges);
    } else {
      this.updateAdjustmentExecutionInfo(subProject, offlineSubProjectChanges);
    }
  }

  updateBaseProjectExecutionInfo(
    subProject: SubProject,
    productChanges: OfflineProductChangeObject[]
  ) {
    if (
      CollectionHelper.isPopulated(productChanges) &&
      subProject.project != null &&
      CollectionHelper.isPopulated(subProject.project.products)
    ) {
      subProject.project.products.forEach((p) => {
        if (p.productPlacement != null) {
          const change = productChanges.find((c) => c.id === p.id);
          if (
            change != null &&
            Object.prototype.hasOwnProperty.call(change, 'executionComplete')
          ) {
            p.productPlacement.executionComplete = change.executionComplete;
          }
        }
      });
    }
  }

  updateAdjustmentExecutionInfo(
    subProject: SubProject,
    subProjectChanges: OfflineSubProjectChangeObject
  ) {
    if (subProject.projectPlacement != null && subProjectChanges != null) {
      subProject.projectPlacement.placementDone =
        subProjectChanges.placementDone;
    }
  }

  calculateSubProjectExecuted(task: SubProjectTask) {
    task.executedClass =
      task.subProject.type === 'BaseProject'
        ? this.calculateBaseProjectExecuted(<BaseProject>task.subProject)
        : this.calculateAdjustmentExecuted(<Adjustment>task.subProject);
    if (task.executedClass === 'pending') {
      task.executedCircle = 'circle-pending';
    } else if (task.executedClass === 'executed') {
      task.executedCircle = 'circle-check';
      task.executedTimeString = GeneralHelper.getMinutesInHourAndMinutes(
        ProjectHelper.getTotalProjectExecutionMinutes(
          task.subProject == null ? null : task.subProject.projectPlacement
        )
      );
    } else {
      task.executedCircle = 'circle-cross';
    }
  }

  calculateBaseProjectExecuted(baseProject: BaseProject): string {
    let allExecuted = false;
    for (const product of baseProject.project.products) {
      if (product.productPlacement.executionComplete == null) {
        return 'pending';
      }
      allExecuted = allExecuted || product.productPlacement.executionComplete;
    }
    return allExecuted ? 'executed' : 'not-executed';
  }

  calculateAdjustmentExecuted(adjustment: Adjustment): string {
    return adjustment.projectPlacement.placementDone == null
      ? 'pending'
      : adjustment.projectPlacement.placementDone
      ? 'executed'
      : 'not-executed';
  }

  calculateSubProjectExecutedIconClass(task: SubProjectTask) {
    task.executedIconClass =
      task.subProject.type === 'BaseProject'
        ? this.calculateBaseProjectExecutedIconClass(
            <BaseProject>task.subProject
          )
        : this.calculateAdjustmentExecutedIconClass(
            <Adjustment>task.subProject
          );
  }

  calculateBaseProjectExecutedIconClass(baseProject: BaseProject): string {
    for (const product of baseProject.project.products) {
      if (
        product.productPlacement.executionComplete == null ||
        product.productPlacement.executionComplete === false
      ) {
        return 'plaatsing';
      }
    }
    return 'uitgevoerd';
  }

  calculateAdjustmentExecutedIconClass(adjustment: Adjustment): string {
    return adjustment.projectPlacement.placementDone == null ||
      adjustment.projectPlacement.placementDone === false
      ? ProjectHelper.getTypeStatusString(adjustment)
      : 'uitgevoerd';
  }

  calculatePlacerNotes(subProjectTask: SubProjectTask): void {
    if (subProjectTask.subProject.type === 'BaseProject') {
      this.calculatePlacerNotesBaseProject(subProjectTask);
    } else {
      this.calculatePlacerNotesAdjustment(subProjectTask);
    }
  }

  calculatePlacerNotesBaseProject(subProjectTask: SubProjectTask): void {
    if (
      CollectionHelper.isPopulated(subProjectTask.subProject.project.products)
    ) {
      for (const product of subProjectTask.subProject.project.products) {
        if (
          product.productPlacement != null &&
          !StringHelper.isEmpty(product.productPlacement.installerInfo)
        ) {
          subProjectTask.placerNotes = product.productPlacement.installerInfo;
          break;
        }
      }
    }
  }

  calculatePlacerNotesAdjustment(subProjectTask: SubProjectTask): void {
    if (
      !StringHelper.isEmpty(
        (<Adjustment>subProjectTask.subProject).withPlacementNotes
      )
    ) {
      subProjectTask.placerNotes = (<Adjustment>(
        subProjectTask.subProject
      )).withPlacementNotes;
    }
  }

  calculateBoardTypeAndPlacement(subProjectTask: SubProjectTask): void {
    if (
      CollectionHelper.isPopulated(
        subProjectTask.subProject.project.products
      ) &&
      ProductHelper.isBoard(subProjectTask.subProject.project.products[0])
    ) {
      const board = <Board>subProjectTask.subProject.project.products[0];
      if (CollectionHelper.isPopulated(board.boardParts)) {
        const boardPart = board.boardParts[0];
        subProjectTask.boardTypeAndPlacement = StringHelper.join(
          [
            boardPart.boardType == null ? null : boardPart.boardType.label,
            boardPart.placementType == null
              ? null
              : boardPart.placementType.label,
          ],
          ' - '
        );
      }
    }
  }

  calculateAppointment(subProjectTask: SubProjectTask): void {
    if (
      subProjectTask.subProject != null &&
      subProjectTask.subProject.projectPlacement != null
    ) {
      if (subProjectTask.subProject.projectPlacement.appointment) {
        subProjectTask.appointmentIcon = 'circle-check';
        subProjectTask.appointmentText = 'Op afspraak';
      } else {
        subProjectTask.appointmentIcon = 'circle-cross';
        subProjectTask.appointmentText = 'Geen afspraak';
      }
    }
  }

  openTask(task: Task) {
    if (task.type === TASK_TYPES.SUB_PROJECT_TASK) {
      this.router.navigateByUrl(
        `/installateur/${task.subProject.project.id}/${task.subProject.id}/${
          this.user.employee.id
        }/${moment(this.date).format('YYYYMMDD')}/${task.id}`
      );
    }
    if (task.type === TASK_TYPES.LOAD_TASK) {
      this.router.navigateByUrl(
        `/installateur/laden/${this.user.employee.id}/${moment(
          this.date
        ).format('YYYYMMDD')}/${task.id}`
      );
    }
    if (task.type === TASK_TYPES.OTHER_TASK) {
      this.router.navigateByUrl(
        `/installateur/taak/${this.user.employee.id}/${moment(this.date).format(
          'YYYYMMDD'
        )}/${task.id}`
      );
    }
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }

  calculateMapAddresses(): void {
    // Ensure we're working with the latest data
    if (this.taskList?.tasks) {
      const calculatedAddresses = TaskAddressHelper.getMapAddresses(
        this.taskList.tasks
      );
      // Only update if we got valid addresses
      if (calculatedAddresses && calculatedAddresses.length > 0) {
        this.addresses = calculatedAddresses;
      }
    }
  }

  setSelectedView(selectedView: SELECTABLE_VIEWS) {
    this.selectedView = selectedView;
  }
}

export enum SELECTABLE_VIEWS {
  PLACER_TOUR_LIST_VIEW = 'placer-tour-list-view',
  PLACER_TOUR_MAP_VIEW = 'placer-tour-map-view',
}
